import { createSlice } from '@reduxjs/toolkit';
import { getEventByIdAsyncThunk, getEventsAsyncThunk, createEventAsyncThunk, updateEventAsyncThunk, deleteEventAsyncThunk, getEventWithoutPaginationAsyncThunk } from './eventThunk';

// Load draft from localStorage if available
const loadDraftFromStorage = () => {
  try {
    const savedDraft = localStorage.getItem('eventDraftFormData');
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch (error) {
    console.error('Error loading event draft from localStorage:', error);
    return null;
  }
};

const initialState = {
  events: [],
  currentEvent: null,
  isLoading: false,
  currentEvent: null,
  isLoading: false,
  error: null,
  draftFormData: loadDraftFromStorage(), // Load draft from localStorage on init
  filters: {
    search: '',
    type: '',
    cpdType: '',
    categoryId: '',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const eventSlice = createSlice({
  name: 'event',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
    // Save draft form data
    saveEventDraft: (state, action) => {
      state.draftFormData = action.payload;
      // Also save to localStorage for persistence across page refreshes
      try {
        localStorage.setItem('eventDraftFormData', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving event draft to localStorage:', error);
      }
    },
    // Clear draft form data
    clearEventDraft: (state) => {
      state.draftFormData = null;
      // Also remove from localStorage
      try {
        localStorage.removeItem('eventDraftFormData');
      } catch (error) {
        console.error('Error clearing event draft from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Event by ID
      .addCase(getEventByIdAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEventByIdAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentEvent = action.payload;
        state.error = null;
      })
      .addCase(getEventByIdAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch event';
      })
      // Get Events
      .addCase(getEventsAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEventsAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.events = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.events = action.payload;
        }
        state.error = null;
      })
      .addCase(getEventsAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch events';
      })
      // Create Event
      .addCase(createEventAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createEventAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events.unshift(action.payload);
        state.error = null;
      })
      .addCase(createEventAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create event';
      })
      // Update Event
      .addCase(updateEventAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEventAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.events.findIndex(event => event._id === action.payload._id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        if (state.currentEvent && state.currentEvent._id === action.payload._id) {
          state.currentEvent = action.payload;
        }
        state.error = null;
      })
      .addCase(updateEventAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update event';
      })
      // Delete Event
      .addCase(deleteEventAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteEventAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = state.events.filter(event => event._id !== action.payload);
        if (state.currentEvent && state.currentEvent._id === action.payload) {
          state.currentEvent = null;
        }
        state.error = null;
      })
      .addCase(deleteEventAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete event';
      })
      // Get Event Without Pagination
      .addCase(getEventWithoutPaginationAsyncThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEventWithoutPaginationAsyncThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload?.data || action.payload || [];
        state.error = null;
      })
      .addCase(getEventWithoutPaginationAsyncThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch events';
      });
  },
});

export const { clearError, clearCurrentEvent, setFilters, setPagination, setCurrentPage, setItemsPerPage, resetPagination, saveEventDraft, clearEventDraft } = eventSlice.actions;
export default eventSlice.reducer;

